import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Avatar, Icon,
  Alert, Stack, Chip, Button, Skeleton, IconButton, Tooltip, Divider,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useKpisEmpresa } from '../../hooks/useDashboard';
import { useCvuDeEmpresa } from '../../hooks/useCvus';
import { useDatosBancarios } from '../../hooks/useDatosBancarios';
import DatosBancariosCard from '../../components/DatosBancariosCard';
import { formatCurrency, formatDate } from '../../utils/format';
import { formatCvu } from '../../types/cvu';

/**
 * Dashboard para la empresa (admin_empresa) — Fase 7.2.
 * KPIs reales + datos de CVU + acceso rápido.
 */
export default function DashboardEmpresa() {
  const { profile, effectiveEmpresaId, impersonatingEmpresaId } = useAuth();

  const empresaId = effectiveEmpresaId ?? undefined;
  const { data: kpis, isLoading: loadingKpis } = useKpisEmpresa(empresaId);
  const { data: cvu, isLoading: loadingCvu } = useCvuDeEmpresa(empresaId);
  const { data: datosBancarios } = useDatosBancarios();

  const [snackbar, setSnackbar] = useState<string | null>(null);
  const copiar = (texto: string, label: string) => {
    void navigator.clipboard.writeText(texto).then(() => {
      setSnackbar(`${label} copiado`);
      window.setTimeout(() => setSnackbar(null), 2000);
    });
  };

  const kpiCards = [
    {
      label: 'Saldo adeudado',
      value: loadingKpis
        ? <Skeleton width={140} height={42} />
        : formatCurrency(kpis?.saldo_total ?? 0),
      icon: 'account_balance_wallet',
      color: Number(kpis?.saldo_total ?? 0) > 0 ? '#c62828' : '#2e7d32',
      link: '/mi-cuenta-corriente',
    },
    {
      label: 'Cupones pendientes',
      value: loadingKpis
        ? <Skeleton width={50} height={42} />
        : String(kpis?.cupones_pendientes ?? 0),
      sub: kpis && kpis.cupones_vencidos > 0 ? `${kpis.cupones_vencidos} vencidos` : undefined,
      subColor: 'error',
      icon: 'receipt_long',
      color: '#ed6c02',
      link: '/cupones',
    },
    {
      label: 'Total pagado',
      value: loadingKpis
        ? <Skeleton width={140} height={42} />
        : formatCurrency(kpis?.monto_pagado_total ?? 0),
      icon: 'payments',
      color: '#2E7D32',
      link: '/mi-cuenta-corriente',
    },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {impersonatingEmpresaId
          ? 'Vista de empresa (impersonando)'
          : `Bienvenido${profile?.nombre ? `, ${profile.nombre}` : ''}`}
      </Typography>

      {/* KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {kpiCards.map((k) => (
          <Grid item xs={12} sm={4} key={k.label}>
            <Card
              component={RouterLink}
              to={k.link}
              sx={{
                borderLeft: `4px solid ${k.color}`,
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${k.color}20`, color: k.color, width: 56, height: 56 }}>
                  <Icon sx={{ fontSize: 30 }}>{k.icon}</Icon>
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {k.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>{k.value}</Typography>
                  {k.sub && (
                    <Typography variant="caption" color={`${k.subColor}.main`}>
                      {k.sub}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Próximo vencimiento */}
      {kpis?.proximo_vencimiento && (
        <Alert
          severity={kpis.cupones_vencidos > 0 ? 'error' : 'warning'}
          sx={{ mb: 3 }}
          action={
            <Button component={RouterLink} to="/cupones" size="small" color="inherit">
              Ver cupones
            </Button>
          }
        >
          Próximo vencimiento: <strong>{formatDate(kpis.proximo_vencimiento)}</strong>
        </Alert>
      )}

      {/* Cuenta bancaria única BNA (ADR #042) */}
      {datosBancarios && <DatosBancariosCard datos={datosBancarios} />}

      {/* CVU: solo si la empresa tiene uno asignado (convive con la cuenta BNA) */}
      {(loadingCvu || cvu || !datosBancarios) && (
      <Card sx={{ borderLeft: '4px solid #1565C0' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Icon color="primary">vpn_key</Icon>
            <Typography variant="h6" fontWeight={600}>
              Pago por CVU
            </Typography>
            <Chip label="Pronto Pago" color="primary" size="small" />
          </Stack>

          {loadingCvu ? (
            <Stack spacing={1}>
              {[1, 2, 3].map((i) => <Skeleton key={i} height={30} />)}
            </Stack>
          ) : !cvu ? (
            <Alert severity="info">
              Tu empresa todavía no tiene CVU asignada. ATSA te avisará cuando
              esté disponible.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Row
                label="Alias"
                value={cvu.alias}
                onCopy={() => copiar(cvu.alias, 'Alias')}
              />
              <Row
                label="CVU"
                value={formatCvu(cvu.cvu)}
                onCopy={() => copiar(cvu.cvu, 'CVU')}
              />
              <Divider />
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  Titular:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ATSA Río Negro
                </Typography>
              </Stack>
              <Alert severity="info" sx={{ fontSize: 13 }}>
                Si tu billetera muestra <strong>"ATSARN"</strong> es la abreviación
                de ATSA Río Negro (Asociación de Trabajadores de la Sanidad
                Argentina, seccional Río Negro). Es el destinatario correcto.
              </Alert>
            </Stack>
          )}
        </CardContent>
      </Card>
      )}

      {snackbar && (
        <Box
          sx={{
            position: 'fixed', bottom: 24, left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'grey.800', color: 'white',
            px: 2, py: 1, borderRadius: 1, zIndex: 1400,
          }}
        >
          <Typography variant="body2">{snackbar}</Typography>
        </Box>
      )}
    </Box>
  );
}

function Row({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
        {label}:
      </Typography>
      <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
        {value}
      </Typography>
      <Tooltip title={`Copiar ${label.toLowerCase()}`}>
        <IconButton size="small" onClick={onCopy}>
          <ContentCopyIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
